import { TrajectorySectionType, WorksheetType } from './models/Trajectory';
import { CurriculumGoalType } from './models/CurriculumGoal';
import { IPageArgs } from '../../modules/student-dashboard/components/StudentAssignments/StudentAssignmentsSection';
import { PlataformGuidePlace } from 'src/store/plataform-guide/types';

export const baseURL = process.env.REACT_APP_API_ROOT_V2 || '';

const apiRoutesV2 = {
  //#region App
  ping: () => `${baseURL}/app/ping`,
  settings: () => `${baseURL}/app/settings`,
  loginV2: () => `${baseURL}/v2/auth/login`,
  ssoSchools: (userName: string, ssoType: string) =>
    `${baseURL}/sso/schools?user_name=${userName}&sso_type=${ssoType}`,
  me: () => `${baseURL}/auth/me`,
  impersonate: (userId: string) => `${baseURL}/v2/auth/impersonate/${userId}`,
  leaveImpersonation: () => `${baseURL}/v2/auth/impersonate`,
  contentPages: (contentPageId: string) => `${baseURL}/app/content-pages/${contentPageId}`,
  microsoftToken: () => `${baseURL}/microsoft/token`,
  contactMail: () => `${baseURL}/users/contact`,
  forgotPassword: () => `${baseURL}/auth/forgot-password`,
  resetPassword: () => `${baseURL}/auth/reset-password`,
  translations: (lang = 'nl-be') => `${baseURL}/translations/${lang}`,
  //#endregion

  //#region Books
  book: (bookId: string) => `${baseURL}/books/${bookId}`,
  studentBookBag: () => `${baseURL}/users/book-bags`,
  getMethods: () => `${baseURL}/methods`,
  booksByUser: () => `${baseURL}/users/books`,
  teacherSummary: () => `${baseURL}/books/teacher-summary`,
  //#endregion

  //#region Book section
  getTeacherBooks: () => `${baseURL}/books/section/my-books`,
  getTeacherAvailableBooks(params: any) {
    const searchParams = new URLSearchParams({
      page: String(params.page) || '1',
      itemsPerPage: String(params.itemsPerPage) || '6',
    });
    return `${baseURL}/books/section/available-books?${searchParams}`;
  },
  getTeacherPreviewBooks(params: any) {
    const searchParams = new URLSearchParams({
      page: String(params.page) || '1',
      itemsPerPage: String(params.itemsPerPage) || '6',
    });
    return `${baseURL}/books/section/preview-books?${searchParams}`;
  },
  getTeacherTraningBooks: () => `${baseURL}/books/section/training-books`,
  toggleBooksSectionOpened: (sectionName: string) => `${baseURL}/users/book-section/${sectionName}`,
  //#endregion

  //#region Chapters
  chapters: (bookId: string) => `${baseURL}/books/${bookId}/chapters`,
  rebuildChapters: (bookId: string) => `${baseURL}/books/${bookId}/chapters/rebuild`,
  chapter: (chapterId: string) => `${baseURL}/chapters/${chapterId}`,
  //#endregion

  // Table of contents (v2)
  tableOfContents: (bookId: string) => `${baseURL}/table-of-contents/books/${bookId}`,
  tableOfContentsChapters: (chapterId: string) =>
    `${baseURL}/table-of-contents/chapters/${chapterId}`,

  // Stepper
  stepper: (key: string, index: string) => `${baseURL}/steppers/${key}/steps/${index}`,

  // Plataform Guide
  plataformGuide: (place: PlataformGuidePlace) => `${baseURL}/platform-guide/${place}`,

  // Search Content
  searchContent: (bookId: string) => `${baseURL}/search-content/${bookId}`,

  //#region Trajectories
  reoderTrajectories: () => `${baseURL}/trajectories/reorder`,
  addTrajectory: (chapterId: string) => `${baseURL}/chapters/${chapterId}/trajectories`,
  trajectories: (bookId: string, chapterId: string) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/trajectories`,
  trajectory: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}`,
  trajectoriesWithDetails: (bookId: string, chapterId: string) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/blocks`,
  patchTrajectoryName: (bookId: string, trajectoryId: string) =>
    `${baseURL}/books/${bookId}/trajectories/${trajectoryId}/alternative-title`,
  groupCanSeeTrajectory: (groupId: string, trajectoryId: string) =>
    `${baseURL}/groups/${groupId}/trajectories/${trajectoryId}/can-see`,
  getTrajectoryScoring: (trajectoryId: string, groupId: string, attempt: string) =>
    `${baseURL}/scoring/trajectories/${trajectoryId}/groups/${groupId}/attempt/${attempt}`,
  moveTrajectory: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}/move`,
  copyTrajectory: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}/copy`,
  differentiations: (trajectoryId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/differentiations`,
  preferredDifferentiation: (trajectoryId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/preferred-differentiation`,
  getTrajectoryToPrint: (trajectoryId: string, pageType: string) =>
    `${baseURL}/trajectories/${trajectoryId}/print/${pageType}`,

  //#endregion

  //#region Toggle trajectory
  toggleTrajectoryPreview: (trajectoryId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/toggle/preview`,
  toggleTrajectoryCollab: (trajectoryId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/toggle/collab`,
  toggleTrajectoryDraft: (trajectoryId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/toggle/draft`,
  toggleTrajectoryFreeze: (trajectoryId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/toggle/freeze`,
  //#endregion

  //#region Pages
  pageDetail: (pageId: string) => `${baseURL}/pages/${pageId}`,
  bookPage: (bookId: string, pageId: string) => `${baseURL}/books/${bookId}/pages/${pageId}`,
  bookTrajectoryPagesRelativePath: (bookId: string, trajectoryId: string) =>
    `/books/${bookId}/trajectories/${trajectoryId}/pages`,
  bookTrajectoryPages: (bookId: string, trajectoryId: string) =>
    `${baseURL}${apiRoutesV2.bookTrajectoryPagesRelativePath(bookId, trajectoryId)}`,
  pages: (trajectoryId: string, type?: TrajectorySectionType | WorksheetType) =>
    `${baseURL}${apiRoutesV2.pagesRelativePath(trajectoryId, type)}`,
  pagesRelativePath: (trajectoryId: string, type?: TrajectorySectionType | WorksheetType) =>
    `/trajectories/${trajectoryId}/pages${type ? `?type=${type}` : ''}`,
  togglePageIsPrintableRelativePath: (bookId: string, pageId: string) =>
    `/books/${bookId}/pages/${pageId}/printable`,
  //#endregion

  //#region Blocks
  addBlock: (bookId: string, pageId: string) => `${baseURL}/books/${bookId}/pages/${pageId}/blocks`,
  blocks: (pageId: string) => `${baseURL}/pages/${pageId}/blocks`,
  block: (bookId: string, pageId: string, blockId: string) =>
    `${baseURL}/books/${bookId}/pages/${pageId}/blocks/${blockId}`,
  toggleBlockIsPrintable: (blockId: string) => `${baseURL}/blocks/${blockId}/printable`,
  orderBlocks: (bookId: string, pageId: string) =>
    `${baseURL}/books/${bookId}/pages/${pageId}/change-block-order`,
  uploadImage: (bookId: string, trajectoryId: string) =>
    `${baseURL}/books/${bookId}/trajectories/${trajectoryId}/image`,
  //#endregion

  //#region Cluster
  cluster: (clusterId: string) => `${baseURL}/clusters/${clusterId}`,
  clusterCreate: () => `${baseURL}/clusters`,
  clusterRelease: (clusterId: string) => `${baseURL}/clusters/${clusterId}/leave-cluster`,
  clusterSplit: (clusterId: string) => `${baseURL}/clusters/${clusterId}/split`,
  //#endregion

  //#region Sections
  sectionCreate: () => `${baseURL}/sections`,
  sectionDelete: (clusterId: string, sectionId: string) =>
    `${baseURL}/sections/${sectionId}/clusters/${clusterId}`,
  //#endregion

  //#region Lock
  lockStatus: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}/check-has-lock`,
  lock: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}/lock`,
  unlock: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}/unlock`,
  //#endregion

  // #region Exercises
  h5p: (exerciseId: string, deepLinking: boolean) =>
    `${baseURL}/h5p/exercises/${exerciseId}?request_type=${
      deepLinking ? 'deep_linking' : 'basic_lti_launch'
    }`,
  h5pDuplicate: (exerciseId: string, duplicateUrl: string) =>
    `${baseURL}/h5p/exercises/${exerciseId}?request_type=duplicate&duplicate_url=${duplicateUrl}`,
  bookWidgets: (exerciseId: string, deepLinking: boolean) =>
    `${baseURL}/bw/exercises/${exerciseId}?request_type=${
      deepLinking ? 'deep_linking' : 'basic_lti_launch'
    }`,
  algebrakit: (exerciseId: string) => `${baseURL}/algebra-kit/exercises/${exerciseId}`,
  algebrakitScore: () => `${baseURL}/algebra-kit/score`,
  //#endregion

  // #region Assignments
  assignments(params: IPageArgs) {
    const searchParams = new URLSearchParams({
      bookId: params.bookFilter || '',
      page: String(params.page),
      groupId: params.groupFilter || '',
    });
    return `${baseURL}/users/assignments?${searchParams}`;
  },
  openStudentAssignments(params: IPageArgs) {
    const searchParams = new URLSearchParams({
      bookId: params.bookFilter || '',
      page: String(params.page),
    });
    return `${baseURL}/users/open-assignments?${searchParams}`;
  },

  closedStudentAssignments(params: IPageArgs) {
    const searchParams = new URLSearchParams({
      bookId: params.bookFilter || '',
      page: String(params.page),
    });
    return `${baseURL}/users/completed-assignments?${searchParams}`;
  },
  addAssignment: () => `${baseURL}/assignments`,
  deleteAssignment: (assignmentId: string) => `${baseURL}/assignments/${assignmentId}`,
  finishAssignment: (assignmentId: string) => `${baseURL}/assignments/${assignmentId}`,
  groupAssignments: (groupId: string, assignmentId: string) =>
    `${baseURL}/groups/${groupId}/assignments/${assignmentId}`,
  // #endregion

  // #region CurriculumGoals
  curriculumGoalsByBook: (bookId: string, type: CurriculumGoalType) =>
    `${baseURL}/books/${bookId}/curriculum-goals?type=${type}`,
  getCurriculumGoalsByTrajectory: (trajectoryId: string, type: string) =>
    `${baseURL}/trajectories/${trajectoryId}/curriculum-goals?type=${type}`,
  curriculumGoals: (bookId: String) => `${baseURL}/books/${bookId}/school-curriculum-goals`,
  addCurriculumGoalToTrajectory: (trajectoryId: string, curriculumGoalId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/curriculum-goals/${curriculumGoalId}`,
  deleteCurriculumGoalFromTrajectory: (
    trajectoryId: string,
    goalId: string,
    type: CurriculumGoalType
  ) => `${baseURL}/trajectories/${trajectoryId}/curriculum-goals/${goalId}?type=${type}`,
  //#region

  getGradesByBook: (bookId: string, groupId?: string) =>
    `${baseURL}/books/${bookId}/grades${groupId ? `?groupId=${groupId}` : ''}`,
  toggleGradesVisibility: (groupId: string, bookId: string) =>
    `${baseURL}/groups/${groupId}/books/${bookId}/toggle/visibility`,
  addCustomGroup: (bookId: string) => `${baseURL}/books/${bookId}/group`,
  deleteCustomGroup: (groupId: string) => `${baseURL}/groups/${groupId}`,
  updateCustomGroup: (groupId: string) => `${baseURL}/groups/${groupId}`,

  //#region Grades
  teacherGrades: () => `${baseURL}/grades`,
  addTeacherToGrade: () => `${baseURL}/grades/teacher`,
  getAvailableGrades: () => `${baseURL}/grades/available`,
  availableMethodesByGrade: (gradeId: string) => `${baseURL}/grades/${gradeId}/methods/available`,
  gradeStudents: (gradeId: string) => `${baseURL}/grades/${gradeId}/students`,
  gradeTeachers: (gradeId: string) => `${baseURL}/grades/${gradeId}/teachers`,
  methodesByGrade: () => `${baseURL}/grades/books`,
  groupStudents: (groupId: string) => `${baseURL}/groups/${groupId}/students`,
  removeTeacherFromGrade: (gradeId: string) => `${baseURL}/teacher/grades/${gradeId}`,
  //#endregion

  //#region Groups
  bookGroups: (bookId: string) => `${baseURL}/books/${bookId}/groups`,
  groupsByUserRelativePath: (userId: string, bookVersionId?: string) =>
    `/users/${userId}/groups${bookVersionId ? `?book_version_id=${bookVersionId}` : ''}`,
  getGroupsManagement: () => `${baseURL}/books/group-management`,
  // end region

  // #region Score
  userPageScores: (pageId: string) => `${baseURL}/pages/${pageId}/scores`,
  groupPageScores: (groupId: string, pageId: string) =>
    `${baseURL}/groups/${groupId}/pages/${pageId}/scores`,
  groupStudentScores: (groupId: string, pageId: string, studentId: string) =>
    `${baseURL}/groups/${groupId}/pages/${pageId}/scores?student=${studentId}`,
  groupScoresByChapter: (groupId: string, bookId: string, chapterId: string) =>
    `${baseURL}/groups/${groupId}/books/${bookId}/chapters/${chapterId}/scores`,
  studentScoresByChapter: (groupId: string, bookId: string, chapterId: string, studentId: string) =>
    `${baseURL}/groups/${groupId}/books/${bookId}/chapters/${chapterId}/scores?student=${studentId}`,
  userScoresByChapter: (bookId: string, chapterId: string) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/scores`,
  // #endregion

  // #region Progress
  groupProgressByChapter: (groupId: string, bookId: string, chapterId: string) =>
    `${baseURL}/groups/${groupId}/books/${bookId}/chapters/${chapterId}/progress`,
  studentProgressByChapter: (
    groupId: string,
    bookId: string,
    chapterId: string,
    studentId: string
  ) =>
    `${baseURL}/groups/${groupId}/books/${bookId}/chapters/${chapterId}/progress?student=${studentId}`,
  pageProgressByTrajectory: (trajectoryId: string, pageId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/pages/${pageId}/progress`,
  userProgressByChapter: (bookId: string, chapterId: string) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/progress`,
  trajectoryProgressByTrajectory: (trajectoryId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/trajectory-progress`,
  // #endregion

  // #region Visibilities
  visibility: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}/visibilities`,
  getVisibilitiesByChapter: (bookId: string, chapterId: string, groupId: string) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/groups/${groupId}/visibilities`,
  patchVisibilityForTrajectory: (trajectoryId: string, groupId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/groups/${groupId}/visibilities`,
  postChapterVisibilities: (bookId: string, chapterId: string) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/visibilities`,
  getTrajectoryGroupVisibilities: (trajectoryId: string) =>
    `${baseURL}/trajectories/${trajectoryId}/group-visibilities`,
  // #endregion

  groupVisibilities: (bookId: string, trajectoryId: string) =>
    `${baseURL}/books/${bookId}/trajectories/${trajectoryId}/groups`,

  // #region Bookmarks
  userBookmarks: () => `${baseURL}/users/bookmarks`,
  studentTrajectoryBookmark: (bookId: string) => `${baseURL}/books/${bookId}/bookmark`,
  postStudentTrajectoryBookmark: (bookId: string, blockId: string) =>
    `${baseURL}/books/${bookId}/blocks/${blockId}/bookmark`,
  getTeacherBookmarksForBook: (blockId: string) => `${baseURL}/books/${blockId}/teacher-bookmark`,
  postTeacherBookmark: (bookId: string, blockId: string) =>
    `${baseURL}/books/${bookId}/blocks/${blockId}/teacher-bookmark`,
  getTeacherBookmarks: () => `${baseURL}/users/teacher-bookmarks`,
  // #endregion

  // Video progress
  studentVideoProgress: (userId: string, blockId: string) =>
    `${baseURL}/users/${userId}/videos?block_id=${blockId}`,
  groupVideoProgress: (groupId: string, blockId: string) =>
    `${baseURL}/groups/${groupId}/videos?block_id=${blockId}`,
  // Data Logging
  dataLogging: () => `${baseURL}/logging/data`,
  // Video logging
  postVideoEvent: () => `${baseURL}/videos/event`,

  // Answers
  userPageAnswers: (user: string, blockId: string) =>
    `${baseURL}/users/${user}/blocks/${blockId}/answer-cards`,
  groupPageAnswers: (group: string, blockId: string) =>
    `${baseURL}/groups/${group}/blocks/${blockId}/answer-cards`,

  // Question Answers (Solutions)
  pageSolutions: (pageId: string) => `${baseURL}/question-answers/page/${pageId}`,

  // #region Feedback
  getTeacherFeedbackByGroup: (
    bookId: string,
    chapterId: string,
    trajectoryId: string,
    groupId: string
  ) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/trajectories/${trajectoryId}/groups/${groupId}/feedback-teacher`,
  getTeacherFeedbackByStudent: (
    bookId: string,
    chapterId: string,
    trajectoryId: string,
    groupId: string,
    studentId: string
  ) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/trajectories/${trajectoryId}/groups/${groupId}/students/${studentId}/feedback-teacher`,
  addFeedback: (bookId: string, chapterId: string, trajectoryId: string, pageId: string) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/trajectories/${trajectoryId}/pages/${pageId}/feedback`,
  readFeedback: (feedbackId: string) => `${baseURL}/feedbacks/${feedbackId}/read`,
  deleteFeedback: (feedbackId: string) => `${baseURL}/feedbacks/${feedbackId}`,
  getStudentFeedback: () => `${baseURL}/users/feedback-student`,
  getStudentFeedbackByBook: (bookId: string) => `${baseURL}/books/${bookId}/feedback-student`,
  getStudentFeedbackByTrajectory: (bookId: string, chapterId: string, trajectoryId: string) =>
    `${baseURL}/books/${bookId}/chapters/${chapterId}/trajectories/${trajectoryId}/feedback-student`,
  // #endregion

  //#endregion Block Notes
  studentNotesByPageId: (pageId: string) => `${baseURL}/pages/${pageId}/block-notes`,
  teacherNotesByBook: (bookId: string, pageId: string) =>
    `${baseURL}/books/${bookId}/pages/${pageId}/teacher-notes`,
  teacherNotesByStudent: (pageId: string, studentId: string) =>
    `${baseURL}/pages/${pageId}/teacher-notes/students/${studentId}`,

  deleteStudentNote: (studentNoteId: string) => `${baseURL}/block-notes/${studentNoteId}`,
  updateStudentNote: (studentNoteId: string = '') => `${baseURL}/block-notes/${studentNoteId}`,

  downloadNotesPerTrajectory: (bookId: string, trajectoryId: string) =>
    `${baseURL}/books/${bookId}/trajectories/${trajectoryId}/block-notes/download`,
  downloadNotesPerPage: (bookId: string, pageId: string) =>
    `${baseURL}/books/${bookId}/pages/${pageId}/block-notes/download`,
  downloadNotesPerGroup: (bookId: string, pageId: string, groupId: string) =>
    `${baseURL}/books/${bookId}/pages/${pageId}/groups/${groupId}/block-notes/download`,
  downloadNotesPerGroupAndTrajectory: (bookId: string, trajectoryId: string, groupId: string) =>
    `${baseURL}/books/${bookId}/trajectories/${trajectoryId}/groups/${groupId}/block-notes/download`,
  downloadNotesPerStudent: (bookId: string, pageId: string, studentId: string) =>
    `${baseURL}/books/${bookId}/pages/${pageId}/student/${studentId}/block-notes/download`,
  downloadNotesPerStudentAndTrajectory: (bookId: string, trajectoryId: string, studentId: string) =>
    `${baseURL}/books/${bookId}/trajectories/${trajectoryId}/student/${studentId}/block-notes/download`,
  //#endregion

  //archive
  getArchive: () => `${baseURL}/archive`,
  // Study Cards
  getCardsByType: (pageId: string, cardType: string) =>
    `${baseURL}/studycards/bytype/${pageId}/${cardType}`,
  createPractice: () => `${baseURL}/study-practice`,
  practiceSummary: (pageId: string) => `${baseURL}/studycards/pages/${pageId}/summary`,
  getNextCard: (studyTrainerPracticeId: string) =>
    `${baseURL}/study-practice/${studyTrainerPracticeId}/next-study-card`,
  sendCardScore: (cardId: string, sessionId: string) =>
    `${baseURL}/studycardscore/${cardId}/sessions/${sessionId}`,
  getSessionScore: (sessionId: string) => `${baseURL}/study-practice/${sessionId}/score`,
  postUploadExcelStudyCards: (pageId: string) =>
    `${baseURL}/studycards/upload/pages/${pageId}/excel`,
  addStudyCards: () => `${baseURL}/studycards`,
  patchStudyCards: (studyCardId: string) => `${baseURL}/studycards/${studyCardId}`,
  deleteStudyCards: (studyCardId: string) => `${baseURL}/studycards/${studyCardId}`,
  reorderStudyCards: (pageId: string) => `${baseURL}/studycards/pages/${pageId}/reorder`,

  // Manual - Paper Digital
  getManual: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}/manual`,
  updateManual: (trajectoryId: string) => `${baseURL}/trajectories/${trajectoryId}/manual`,
  //others
  toggleImmersiveReader: () => `${baseURL}/users/immersive-reader`,

  // Hubspot
  getHubspotIdentificationToken: () => `${baseURL}/hubspot/token`,

  // news
  getNotifications: (status: boolean) =>
    `${baseURL}/notifications?unRead=${status}&page=1&PageSize=100`,
  readNotifications: (id: string) => `${baseURL}/notifications/${id}`,

  // survey
  survey: (key: string) => `${baseURL}/surveys/${key}`,
  answerdExternalSurvey: (key: string) => `${baseURL}/surveys/${key}/external-survey`,
};

export default apiRoutesV2;
